import { Country } from 'country-state-city';
import { Logger } from 'ionic-logging-service';

import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { IonModal } from '@ionic/angular';
import * as BlinkCard from '@microblink/blinkcard-capacitor';
import { BasePage } from '@pages/base.page';
import { BLINK_CARD_LICENSE_KEYS, CREDIT_CARD_REGEX, CVV_REGEX } from '@shared/constants';
import { LoggerService } from '@shared/services/core/logger.service';
import {
    ActiveReservationRepository
} from '@shared/state/reservations/active-reservation.repository';

interface CustomerCard {
	firstName: string;
	lastName: string;
	address: string;
	state: string;
	postalCode: string;
}

@Component({
	selector: 'payment-form-modal',
	templateUrl: 'payment-form.component.html',
})
export class PaymentFormComponent extends BasePage implements OnInit {
	@Input('customer') customer: CustomerCard = {
		firstName: this.env.fill('randFullName', { withAccents: false }),
		lastName: '',
		address: this.env.fill('randStreetAddress'),
		state: this.env.fill('randStateAbbr'),
		postalCode: `${this.env.fill('randNumber', { min: 30000, max: 39999 })}`,
	};
	@Input('showScanner') showScanner: boolean = true;
	@Output() onPaymentFormCompletion: EventEmitter<any> = new EventEmitter();
	@Output() onPaymentFormCancel: EventEmitter<any> = new EventEmitter();
	public form: FormGroup;
	public states = [];
	public countries = Country.getAllCountries();
	public hideAddressFields = false;
	private log: Logger;
	private customerData: any;

	@ViewChild(IonModal) modal: IonModal;
	@Output() onModalClose: EventEmitter<any> = new EventEmitter();

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private readonly formBuilder: FormBuilder,
		private readonly loggingService: LoggerService,
		private readonly activeRepo: ActiveReservationRepository
	) {
		super();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - ngOnInit
	 **-------------------------------------------------------------------------------------
	 */
	async ngOnInit() {
		this.activeRepo.selectActiveLaunch$.subscribe(resp => {
			this.customerData = {};
		});
		this.setupForm();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - setupForm
	 **-------------------------------------------------------------------------------------
	 */
	public setupForm() {
		this.form = this.formBuilder.group(
			{
				cardNumber: [this.env.fill('randCreditCardNumber'), [Validators.required, Validators.pattern(CREDIT_CARD_REGEX)]],
				nameOnCard: [`${this.customer?.firstName} ${this.customer?.lastName}`, [Validators.required]],
				expirationMonth: [this.env.fill('randMonth', { min: 1, max: 12 }), [Validators.required]],
				expirationYear: [this.env.fill('randNumber', { min: 24, max: 29 }), [Validators.required]],
				cvv: [
					this.env.fill('randCreditCardCVV'),
					[Validators.required, Validators.pattern(CVV_REGEX), Validators.maxLength(4), Validators.minLength(3)],
				],
			},
			{
				validators: [this.expirationDateValidator],
			}
		);

		this.form.statusChanges.subscribe(resp => {
			this.form.value.expirationDate = `${this.form.get('expirationMonth').value}/${this.form.get('expirationYear').value}`;
		});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - onWillDismiess
	 **-------------------------------------------------------------------------------------
	 */
	onWillDismiss(event: Event) {}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - initializeScanner
	 **-------------------------------------------------------------------------------------
	 */
	async initializeScanner() {
		this.log = this.loggingService.getLogger(`SCANNER:CREDITCARD`);
		const plugin = new BlinkCard.BlinkCardPlugin();
		const blinkCardRecognizer = new BlinkCard.BlinkCardRecognizer();
		blinkCardRecognizer.returnFullDocumentImage = true;
		new BlinkCard.BlinkCardOverlaySettings();
		const scanningResults = await plugin.scanWithCamera(
			new BlinkCard.BlinkCardOverlaySettings(),
			new BlinkCard.RecognizerCollection([blinkCardRecognizer]),
			BLINK_CARD_LICENSE_KEYS
		);
		if (Array.isArray(scanningResults) && scanningResults.length) {
			const [card] = scanningResults;
			if (!card.cardNumberValid) {
				this.alert({
					header: 'Card Not Valid',
					message: 'Card number not valid, please enter in manually',
				});
				return;
			}
			this.form.get('cardNumber').setValue(card.cardNumber.replace(/ /gi, ''));
			this.form.get('cvv').setValue(card.cvv);
			this.form.get('expirationMonth').setValue(`${card.expiryDate.month < 10 ? '0' : ''}${card.expiryDate.month}`);
			this.form.get('expirationYear').setValue(Number(`${card.expiryDate.year.toString().slice(-2)}`));
			this.form.value.expirationDate = `${this.form.get('expirationMonth').value}/${this.form.get('expirationYear').value}`;
			this.log?.info(`Credit Card Scanner Used`, []);
			return;
		}
		this.alert({
			header: 'Unable To Read Card',
			message: 'Unable to read credit card, please enter in manually.',
		});
		return;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - open
	 **-------------------------------------------------------------------------------------
	 */
	public present() {
		this.modal.backdropDismiss = false;
		this.modal.present();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveCard
	 **-------------------------------------------------------------------------------------
	 */
	public saveCard() {
		this.onPaymentFormCompletion.emit({
			valid: true,
			payload: {
				...this.form.value,
				cvv: `${this.form.value.cvv}`,
			},
		});
		this.close();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - cancel
	 **-------------------------------------------------------------------------------------
	 */
	public close() {
		this.form.reset();
		this.onPaymentFormCancel.emit({
			cancel: true,
		});
		this.modal.dismiss(null, 'close');
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - trackBy trackBy:trackBy
	 **-------------------------------------------------------------------------------------
	 */
	public trackByCountries(index, item) {
		return item.name;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - trackBy trackBy:trackBy
	 **-------------------------------------------------------------------------------------
	 */
	public trackByStates(index, item) {
		return item.name;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - expirationDateValidator
	 **-------------------------------------------------------------------------------------
	 */
	private expirationDateValidator: ValidatorFn = (group: FormGroup): ValidationErrors | null => {
		const currentYear = +new Date().getFullYear().toString().slice(-2);
		const currentMonth = new Date().getMonth() + 1;

		const expirationYear = +group.get('expirationYear').value;
		const expirationMonth = +group.get('expirationMonth').value;

		if (expirationYear < currentYear || (expirationYear === currentYear && expirationMonth < currentMonth)) {
			return { invalidExpiration: true };
		}
		return null;
	};
}
